import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, LogOut, Calendar, TrendingUp, Users } from 'lucide-react';

interface Project {
  id: string;
  clientId: string;
  name: string;
  type: string;
  status: 'active' | 'planning' | 'completed';
  logo: string;
  responsible: string;
  deadline: string;
  progress: number;
  route: string;
}

const PROJECTS: Project[] = [
  {
    id: 'pictet',
    clientId: 'pictet',
    name: 'Banque PICTET - Cloud Migration',
    type: 'Infrastructure Bancaire',
    status: 'active',
    logo: '/logos/pictet.png',
    responsible: 'Jean-Pierre Moreau',
    deadline: '15 Octobre 2025',
    progress: 42,
    route: '/portail-client/pictet'
  },
  {
    id: 'richemont',
    clientId: 'richemont',
    name: 'Richemont - Digital Luxury',
    type: 'E-commerce & Analytics',
    status: 'active',
    logo: '/logos/richemont.png',
    responsible: 'Sophie Dubois',
    deadline: '30 Septembre 2025',
    progress: 78,
    route: '/portail-client/richemont'
  },
  {
    id: 'sword',
    clientId: 'sword',
    name: 'Sword Tech - AI Platform',
    type: 'Intelligence Artificielle',
    status: 'planning',
    logo: '/logos/sword.png',
    responsible: 'Marc Andersen',
    deadline: 'Q4 2025',
    progress: 15,
    route: '/portail-client/sword'
  },
  {
    id: 'caterpillar',
    clientId: 'caterpillar',
    name: 'Caterpillar - IoT Industrial',
    type: 'Industry 4.0',
    status: 'active',
    logo: '/logos/caterpillar.png',
    responsible: 'Thomas Mueller',
    deadline: '15 Décembre 2025',
    progress: 58,
    route: '/portail-client/caterpillar'
  },
  {
    id: 'bmw',
    clientId: 'bmw',
    name: 'BMW - Connected Drive 2.0',
    type: 'Automobile Connectée',
    status: 'active',
    logo: '/logos/bmw.png',
    responsible: 'Klaus Schmidt',
    deadline: '28 Février 2026',
    progress: 23,
    route: '/portail-client/bmw'
  },
  {
    id: 'mcdonalds',
    clientId: 'mcdonalds',
    name: 'McDonald\'s - Smart Restaurants',
    type: 'Digital Transformation',
    status: 'active',
    logo: '/logos/mcdonalds.png',
    responsible: 'Marie Laurent',
    deadline: '30 Novembre 2025',
    progress: 67,
    route: '/portail-client/mcdonalds'
  },
  {
    id: 'pernod',
    clientId: 'pernod',
    name: 'Pernod Ricard - Supply Chain AI',
    type: 'Logistique Intelligente',
    status: 'planning',
    logo: '/logos/pernod.png',
    responsible: 'Philippe Martin',
    deadline: 'Q1 2026',
    progress: 8,
    route: '/portail-client/pernod'
  },
  {
    id: 'lombard',
    clientId: 'lombard',
    name: 'Lombard Odier - Wealth Tech',
    type: 'FinTech Innovation',
    status: 'active',
    logo: '/logos/lombard.png',
    responsible: 'Alexandra Rousseau',
    deadline: '31 Janvier 2026',
    progress: 34,
    route: '/portail-client/lombard'
  },
  {
    id: 'formanova',
    clientId: 'formanova',
    name: 'Formanova - Formation IA',
    type: 'Éducation & Formation',
    status: 'active',
    logo: '/logos/formanova.png',
    responsible: 'Davek',
    deadline: '30 Juin 2026',
    progress: 10,
    route: '/portail-client/formanova'
  }
];

const ClientPortal = () => {
  const { user, login, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const success = await login(email, password);
    if (!success) {
      setError('Email ou mot de passe incorrect');
    }
  };

  // Si pas connecté, afficher login
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-sm w-full space-y-6 p-8 bg-white rounded-2xl shadow-2xl border border-gray-200">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-600 to-purple-700 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Accès Client</h2>
            <p className="text-gray-500 text-sm">Portail sécurisé</p>
          </div>
          
          <form className="space-y-4" onSubmit={handleLogin}>
            <div className="space-y-3">
              <input
                type="email"
                placeholder="Email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              
              <input
                type="password"
                placeholder="Mot de passe"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            {error && (
              <p className="text-red-600 text-sm text-center">{error}</p>
            )}
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:scale-105"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
            >
              {isLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
          
        </div>
      </div>
    );
  }

  // Dashboard avec tuiles
  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Dashboard Projets Stratégiques
              </h1>
              <p className="text-base text-gray-600 mt-1">
                Bienvenue, {user.name}
              </p>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 transition"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Projects Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROJECTS.map(project => {
            const hasAccess = user.role === 'admin' || user.client === project.clientId;
            
            return (
              <div
                key={project.id}
                onClick={() => {
                  console.log('Click sur tuile:', project.name, 'hasAccess:', hasAccess, 'route:', project.route);
                  if (hasAccess) {
                    console.log('Navigation vers:', project.route);
                    navigate(project.route);
                  }
                }}
                className={`
                  relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300
                  ${hasAccess 
                    ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' 
                    : 'cursor-not-allowed'
                  }
                `}
              >
                {/* Card Content */}
                <div className={!hasAccess ? 'filter blur-[12px] select-none pointer-events-none opacity-40' : ''}>
                  {/* Header */}
                  <div className="p-6 pb-0">
                    <div className="flex justify-between items-start mb-4">
                      <img 
                        src={project.logo} 
                        alt={project.name}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = `https://via.placeholder.com/100x50/0066cc/ffffff?text=${project.name.split(' ')[0]}`;
                        }}
                      />
                      <span className={`
                        px-3 py-1.5 rounded-full text-sm font-medium
                        ${project.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : project.status === 'planning'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                        }
                      `}>
                        {project.status === 'active' ? 'En cours' : 
                         project.status === 'planning' ? 'Planification' : 'Terminé'}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {project.name}
                    </h3>
                    <p className="text-base text-gray-600 mb-4">{project.type}</p>
                  </div>

                  {/* Progress */}
                  <div className="px-6 pb-4">
                    <div className="flex justify-between text-base mb-2">
                      <span className="text-gray-600">Progression</span>
                      <span className="font-medium text-gray-900">{project.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 bg-gray-50 border-t">
                    <div className="flex justify-between text-base">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-5 h-5" />
                        <span className="text-sm">{project.responsible}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-5 h-5" />
                        <span className="text-sm font-medium">{project.deadline}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default ClientPortal;